import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, Check, Banknote, CreditCard, Truck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQuantity, removeItem, freeShippingRemaining, freeShippingProgress, FREE_SHIPPING_THRESHOLD, checkout, payWithCard } = useStore();
  const [checkoutMode, setCheckoutMode] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", county: "", address: "" });
  const [payMethod, setPayMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(null);

  const close = () => { setCartOpen(false); setTimeout(() => { setCheckoutMode(false); setDone(null); setErrors({}); }, 400); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Numele complet este obligatoriu.";
    if (!form.phone.trim()) e.phone = "Numărul de telefon este obligatoriu.";
    else if (!/^[0-9+()\s.-]{7,}$/.test(form.phone.trim())) e.phone = "Număr de telefon invalid.";
    if (!form.city.trim()) e.city = "Orașul este obligatoriu.";
    if (!form.county.trim()) e.county = "Județul este obligatoriu.";
    if (!form.address.trim()) e.address = "Adresa de livrare este obligatorie.";
    if (form.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Adresă de email invalidă.";
    return e;
  };

  const setField = (k, v) => {
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Verifică datele de livrare — unele câmpuri lipsesc sau sunt incorecte.");
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      if (payMethod === "card") {
        const res = await payWithCard({ ...form });
        if (res?.checkout_url) { window.location.href = res.checkout_url; return; }
        toast.error("Nu s-a putut iniția plata cu cardul.");
      } else {
        const res = await checkout({ ...form, payment_method: "Cash" });
        setDone(res);
        setForm({ name: "", email: "", phone: "", city: "", county: "", address: "" });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "A apărut o eroare la plasarea comenzii.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} className="fixed inset-0 z-[60] bg-ink/40 backdrop-blur-sm" />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 32 }}
            className="fixed inset-y-0 right-0 z-[70] w-full max-w-md bg-cream shadow-2xl border-l border-line flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-line">
              <div className="flex items-center gap-3">
                <ShoppingBag size={18} strokeWidth={1.5} />
                <h3 className="font-serif text-2xl text-ink">{done ? "Comandă plasată" : checkoutMode ? "Finalizare comandă" : "Coșul tău"}</h3>
              </div>
              <button data-testid="cart-close-btn" onClick={close} className="text-ink hover:text-mocha-dark transition-colors"><X size={22} strokeWidth={1.5} /></button>
            </div>

            {done ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-lavender flex items-center justify-center text-lavender-dusty"><Check size={30} /></div>
                <h4 className="font-serif text-3xl text-ink">Mulțumim!</h4>
                <p className="text-ink-soft">{done.message}</p>
                <p className="text-xs text-ink-muted">Cod comandă: {done.order_id?.slice(0, 8).toUpperCase()}</p>
                <button onClick={close} className="mt-4 px-8 py-3.5 bg-ink text-cream text-xs uppercase tracking-[0.2em] hover:bg-mocha-dark transition-colors">Închide</button>
              </div>
            ) : cart.items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8">
                <ShoppingBag size={40} strokeWidth={1} className="text-mocha" />
                <p className="text-ink-soft" data-testid="cart-empty-msg">Coșul tău este gol momentan.</p>
                <button onClick={close} className="px-8 py-3.5 border border-ink/30 text-ink text-xs uppercase tracking-[0.2em] hover:bg-ink/5 transition-colors">Descoperă parfumurile</button>
              </div>
            ) : checkoutMode ? (
              <form onSubmit={submitOrder} className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
                <p className="text-sm text-ink-soft mb-2">Introdu datele pentru livrare</p>
                {[
                  ["name", "Nume complet", "text", true],
                  ["email", "Email (opțional)", "email", false],
                  ["phone", "Număr de telefon", "tel", true],
                  ["city", "Oraș", "text", true],
                  ["county", "Județ", "text", true],
                  ["address", "Adresă de livrare", "text", true],
                ].map(([k, label, type, required]) => (
                  <div key={k}>
                    <label className="eyebrow text-[0.6rem] block mb-2">{label}{required ? <span className="text-mocha-dark"> *</span> : null}</label>
                    <input data-testid={`checkout-${k}`} type={type} value={form[k]} onChange={(e) => setField(k, e.target.value)}
                      aria-invalid={!!errors[k]}
                      className={`w-full bg-surface border px-4 py-3 text-ink outline-none transition-colors ${errors[k] ? "border-destructive focus:border-destructive" : "border-line focus:border-mocha-dark"}`} />
                    {errors[k] ? <p data-testid={`checkout-error-${k}`} className="text-xs text-destructive mt-1.5">{errors[k]}</p> : null}
                  </div>
                ))}

                <div>
                  <label className="eyebrow text-[0.6rem] block mb-2">Livrare</label>
                  <div data-testid="shipping-dpd-box" className="flex items-center gap-4 bg-ink text-cream px-4 py-4 rounded-sm">
                    <span className="w-11 h-11 flex items-center justify-center bg-cream/15 rounded-full text-[#EBC98A]">
                      <Truck size={20} strokeWidth={1.5} />
                    </span>
                    <div className="flex-1">
                      <p className="font-medium">DPD Express - Transport</p>
                      <p className="text-xs text-cream/70">Livrare rapidă prin curier DPD.</p>
                    </div>
                    <span data-testid="shipping-amount" className="font-serif text-lg text-[#EBC98A]">
                      {cart.subtotal >= FREE_SHIPPING_THRESHOLD ? "GRATUIT" : "20 RON"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="eyebrow text-[0.6rem] block mb-2">Modalitate de plată</label>
                  <div className="space-y-3">
                    <button type="button" data-testid="payment-method-card" onClick={() => setPayMethod("card")}
                      className={`w-full flex items-center gap-4 border-2 px-4 py-4 rounded-sm text-left transition-colors ${payMethod === "card" ? "border-mocha-dark bg-surface" : "border-line bg-transparent hover:border-mocha"}`}>
                      <span className="w-11 h-11 flex items-center justify-center bg-mocha-dark/10 rounded-full text-mocha-dark">
                        <CreditCard size={20} strokeWidth={1.5} />
                      </span>
                      <div className="flex-1">
                        <p className="text-ink font-medium">Card bancar (online)</p>
                        <p className="text-xs text-ink-muted">Plată securizată prin Stripe. Vei fi redirecționat.</p>
                      </div>
                      {payMethod === "card" && <span className="w-5 h-5 rounded-full bg-mocha-dark flex items-center justify-center text-cream"><Check size={13} strokeWidth={3} /></span>}
                    </button>

                    <button type="button" data-testid="payment-method-cash" onClick={() => setPayMethod("cash")}
                      className={`w-full flex items-center gap-4 border-2 px-4 py-4 rounded-sm text-left transition-colors ${payMethod === "cash" ? "border-mocha-dark bg-surface" : "border-line bg-transparent hover:border-mocha"}`}>
                      <span className="w-11 h-11 flex items-center justify-center bg-mocha-dark/10 rounded-full text-mocha-dark">
                        <Banknote size={20} strokeWidth={1.5} />
                      </span>
                      <div className="flex-1">
                        <p className="text-ink font-medium">Cash la livrare</p>
                        <p className="text-xs text-ink-muted">Plătești în numerar la primirea coletului.</p>
                      </div>
                      {payMethod === "cash" && <span className="w-5 h-5 rounded-full bg-mocha-dark flex items-center justify-center text-cream"><Check size={13} strokeWidth={3} /></span>}
                    </button>
                  </div>
                </div>

                <div className="pt-4 space-y-2 border-t border-line">
                  <div className="flex items-center justify-between text-sm text-ink-soft">
                    <span>Subtotal</span><span>{formatPrice(cart.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-ink-soft">
                    <span>Transport (DPD Express)</span>
                    <span data-testid="checkout-shipping">{cart.subtotal >= FREE_SHIPPING_THRESHOLD ? "Gratuit" : formatPrice(20)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 text-ink">
                    <span className="text-sm uppercase tracking-widest">Total</span>
                    <span data-testid="checkout-total" className="font-serif text-2xl">{formatPrice(cart.subtotal + (cart.subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : 20))}</span>
                  </div>
                </div>
                <button data-testid="place-order-btn" type="submit" disabled={submitting}
                  className="w-full py-4 bg-ink text-cream text-xs uppercase tracking-[0.25em] hover:bg-mocha-dark transition-colors disabled:opacity-60">
                  {submitting ? "Se procesează..." : payMethod === "card" ? "Plătește cu cardul" : "Plasează comanda"}
                </button>
                <button type="button" onClick={() => setCheckoutMode(false)} className="w-full py-3 text-ink-soft text-xs uppercase tracking-widest hover:text-ink">Înapoi la coș</button>
              </form>
            ) : (
              <>
                {/* Free shipping progress */}
                <div className="px-6 py-4 bg-beige/50 border-b border-line/60">
                  <p className="text-xs text-ink-soft mb-2">
                    {freeShippingRemaining > 0
                      ? <>Mai adaugă <span className="text-ink font-medium">{formatPrice(freeShippingRemaining)}</span> pentru livrare gratuită</>
                      : <span className="text-mocha-dark font-medium">Felicitări! Beneficiezi de livrare gratuită.</span>}
                  </p>
                  <div className="h-1 bg-line rounded-full overflow-hidden">
                    <motion.div className="h-full bg-mocha-dark" initial={{ width: 0 }} animate={{ width: `${freeShippingProgress}%` }} transition={{ duration: 0.6 }} />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                  {cart.items.map((it) => (
                    <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="flex gap-4">
                      <div className="w-20 h-24 bg-beige overflow-hidden flex-shrink-0">
                        <img src={it.product.image} alt={it.product.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="eyebrow text-[0.55rem] text-mocha-dark">{it.product.brand}</p>
                        <h4 className="font-serif text-lg text-ink leading-tight truncate">{it.product.name}</h4>
                        <p className="text-sm text-ink mt-1">{formatPrice(it.product.price)}</p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-line">
                            <button data-testid={`qty-decrease-${it.product_id}`} onClick={() => updateQuantity(it.product_id, it.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-ink hover:bg-beige transition-colors"><Minus size={13} /></button>
                            <span className="w-8 text-center text-sm">{it.quantity}</span>
                            <button data-testid={`qty-increase-${it.product_id}`} onClick={() => updateQuantity(it.product_id, it.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-ink hover:bg-beige transition-colors"><Plus size={13} /></button>
                          </div>
                          <button data-testid={`remove-item-${it.product_id}`} onClick={() => removeItem(it.product_id)} className="text-ink-muted hover:text-destructive transition-colors"><Trash2 size={16} strokeWidth={1.5} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="px-6 py-5 border-t border-line space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm uppercase tracking-widest text-ink-soft">Subtotal</span>
                    <span data-testid="cart-subtotal" className="font-serif text-2xl text-ink">{formatPrice(cart.subtotal)}</span>
                  </div>
                  <button data-testid="checkout-btn" onClick={() => setCheckoutMode(true)}
                    className="w-full py-4 bg-ink text-cream text-xs uppercase tracking-[0.25em] hover:bg-mocha-dark transition-all duration-500">
                    Finalizează comanda
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
